import { LitElement, property, internalProperty, html } from 'lit-element'
import { ifDefined } from 'lit-html/directives/if-defined'
import { connect } from 'pwa-helpers/connect-mixin'
import _ from 'underscore'
import RegisterDispatchers from './dispatchers'
import Backbone from '@polleverywhere/backbone'
import { bemBlock } from '@polleverywhere/bem-helpers'
import User from '@polleverywhere/core/lib/models/user'
import Participant from '@polleverywhere/core/lib/models/participant'
import { register } from '@polleverywhere/hmr-custom-element'
import store from '@polleverywhere/redux'
import { slice as appSlice } from './slices/app'
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
import { notify, clearAll } from './components/notification_manager'
import CookieConsentManager from './components/cookie_consent_manager'
import { getContent } from './pages'
import { getState } from './states'
import './components/nav_wrapper'
import './app.scss'
import startSession from './util/start_session'
import destroySession from './util/destroy_session'
import listenToSelector from './util/listen_to_selector'

store.addReducers({
  [appSlice.name]: appSlice.reducer,
  [profileSlice.name]: profileSlice.reducer
})

function enableServiceWorker () {
  if ('serviceWorker' in navigator) {
    try {
      navigator.serviceWorker.register('/service-worker.js')
        .then(registration => {
          console.log('SW registered: ', registration)
        })
        .catch((err) => {
          console.log('SW registration failed: ', err)
        })
    } catch (registrationError) {
      console.log('SW registration failed: ', registrationError)
    }
  }
}

function offlineError () {
  notify(this, {
    title: 'Error loading page',
    content: 'Please check your internet connection and try again',
    type: 'error',
    action: 'Reload page',
    onAction () { window.location.reload() }
  })
}

function unknownError () {
  notify(this, {
    title: 'Aww snap! Something broke',
    content: 'We\'ve been notified about this issue and we\'ll take a look at it shortly.',
    type: 'error',
    action: 'Reload page',
    onAction () { window.location.reload() }
  })
}

async function updatePage (state, page) {
  const { previousPage, defaultPage } = store.getState()[appSlice.name]

  try {
    const lastPage = previousPage || defaultPage
    // sometimes getContent aborts before rendering anything, so we only want to
    // assign a value when there is one
    const content = await getContent.call(this, state, page, lastPage, this._pageOptions)
    if (content) {
      this.content = content

      // clear out page options after render
      this._pageOptions = {}
    }
  } catch (e) {
    // Unless it's the chunk loading error (network issue) let Rollbar know
    const chunkFailedMessage = /Loading.*chunk [\d]+ failed/
    if (chunkFailedMessage.test(e.message)) {
      offlineError.call(this)
    } else {
      unknownError.call(this)
      window.Rollbar?.error('Unable to update page', e)
      console.error('Unable to update page', e)
    }
  }
}

function changeTheme () {
  const theme = this.state.get('settings').get('theme')

  this.theme = theme
  this.trigger('change:theme', theme)
}

function startApp () {
  // Get a new state
  resetState.call(this)

  const { options } = store.getState()[appSlice.name]

  if (options.shouldStartSessionOnStart) {
    // Pre-emptively load the default page
    store.dispatch(appSlice.pageUpdatedToDefault())

    // Start fetching profile data
    startSession(options.sessionAuthToken)
  }

  listenToSelector((state) => appSlice.selectIsSessionStarted(state), (sessionStarted) => {
    const state = store.getState()
    const { ready, options } = state[appSlice.name]

    if (!ready) {
      // Start router if requested
      if (!options.router) {
        // Show default page
        store.dispatch(appSlice.pageUpdatedToDefault())
        return
      }

      this.cookieConsentManager?.showBannerUntilCookiesAccepted()

      // re-render the current page when logging in
      updatePage.call(this, this.state, this._page)

      // Session started. Set app to ready state
      store.dispatch(appSlice.appReady())
    }
  })

  listenToSelector((state) => profileSlice.selectIsLoggedIn(state), (loggedIn) => {
    if (loggedIn) {
      const { page, previousPage } = store.getState()[appSlice.name]

      // logged in
      if (['login', 'log in', 'signup'].includes(page)) {
        store.dispatch(appSlice.pageUpdated(previousPage))
      }

      // TODO: do we still need this?
      // const { role, mode } = store.getState()[appSlice.name]
      // if (mode !== 'integration' && role === 'presenter' && this.state.messageHistory().shouldIntro('welcome')) {
      //   const onAction = () => {
      //     this.state.appStates.activities.startAction('create')
      //     this.state.messageHistory().trackIntro('welcome')
      //     return true
      //   }

      //   const onDismiss = () => {
      //     this.state.messageHistory().trackIntro('welcome')
      //   }

      //   notify(this, {
      //     title: 'Welcome!',
      //     content: 'Get started by creating an activity',
      //     type: 'info',
      //     action: 'Create activity',
      //     onAction,
      //     onDismiss
      //   })
      // }
    } else {
      // logged out
      clearAll(this)

      this.theme = 'auto'
      this.trigger('change:theme', 'auto')

      // Initialize new state
      resetState.call(this)

      startSession()
    }
  })
}

function toggleReady () {
  const { ready, options } = store.getState()[appSlice.name]
  this.hidden = !ready && options.shouldStartSessionOnStart
}

function resetState (opts = {}) {
  // Stop listening to old state
  if (this.state) {
    this.stopListening(this.state)
    this.state.off()
    this.state.destroy?.()
  }

  const { role, mode } = store.getState()[appSlice.name]

  // create new state
  _.defaults(opts, {
    // instantiate user
    user: new User(),
    participant: new Participant(),
    role,
    mode
  })

  this.state = getState(opts)

  store.dispatch(appSlice.pageUpdatedToDefault())

  this.listenTo(this.state, 'change:theme', changeTheme.bind(this))

  // Make sure we're visible if we're coming from initial load
  toggleReady.call(this)

  this.listenTo(this.state.get('user'), 'change:superuser', (user, superuser) => {
    const superuserWarningId = 'superuser-warning'

    if (superuser) {
      const superuserWarning = document.createElement('div')
      superuserWarning.id = superuserWarningId
      superuserWarning.innerHTML = `Superuser ${superuser.name} logged in as ${user.fullName()}. <a href="/logout">Please log out</a> when finished.`
      this.appendChild(superuserWarning)
    } else {
      const superuserWarning = document.getElementById(superuserWarningId)

      if (superuserWarning) {
        this.removeChild(superuserWarning)
      }
    }
  })

  this.listenTo(this.state, 'notify:error', (type, originalError) => {
    if (type === 'offline') {
      offlineError.call(this)
    } else {
      unknownError.call(this)
    }
  })

  // Let consumers know we've reset the state
  this.trigger('reset:state', this.state)
}

async function initialize () {
  const { serviceWorker } = store.getState()[appSlice.name].options
  const { role, mode } = store.getState()[appSlice.name]

  await RegisterDispatchers(this)
  startApp.call(this)

  if (serviceWorker) {
    enableServiceWorker.call(this)
  }

  if (CookieConsentManager.requiredFor(role, mode)) {
    this.cookieConsentManager = new CookieConsentManager({ app: this })
    // TODO: convert this to litelement and move to render()
    document.body.appendChild(this.cookieConsentManager.render().el)
    this.cookieConsentManager.once('allow_cookies', () => this.trigger('allow_cookies'))
  } else {
    this.trigger('allow_cookies')
  }
}

function handleActivated (e) {
  const route = e.target?.activeItem?.name
  if (!route) return

  // TODO(L2): find a better way to handle L2 nav which doesn't involve the root app
  // understanding things about the activities slice and assuming that's the only L2
  const isL2 = e.target.closest('pe-navigation-panel')
  if (isL2) {
    const l1Page = e.target.closest('pe-navigation').active

    this.dispatchEvent(new CustomEvent('l2-activated', {
      bubbles: true,
      cancelable: true,
      detail: { l1Page, route }
    }))

    store.dispatch(appSlice.pageUpdated(l1Page))

    return
  }

  this.dispatchEvent(new CustomEvent('l1-activated', {
    bubbles: true,
    cancelable: true,
    detail: { route }
  }))

  store.dispatch(appSlice.pageUpdated(route))
}

/**
 * Main application View
 *
 * @event reset:state State was reset
 *  @property {Model} state New instance of state model
 *
 */
export default class App extends connect(store)(LitElement) {
  static tagName = 'pe-application'

  @property({ type: Boolean, reflect: true })
  hidden = true

  /**
   * @type {Backbone.View|HTMLElement|TemplateResult}
   */
  @internalProperty({ type: Object })
  content

  @property({ type: String, reflect: true, attribute: 'data-role' })
  role

  @property({ type: String, reflect: true, attribute: 'data-theme' })
  theme = 'auto'

  /**
   * Allow overriding the navigation active item, format is
   * {rootPage}/{appPage}/{appView}
   */
  @property({ type: String })
  activePath = ''

  set options (opts) {
    if (opts.mode) {
      store.dispatch(appSlice.modeUpdated(opts.mode))
      delete opts.mode
    }

    if (opts.role) {
      store.dispatch(appSlice.roleUpdated(opts.role))
      delete opts.role
    }

    store.dispatch(appSlice.optionsUpdated(opts))
  }

  get options () {
    return store.getState()[appSlice.name].options
  }

  _page
  _pageOptions

  createRenderRoot () { return this }

  constructor () {
    super()

    // For backwards compatibility until we migrate this element off backbone events
    _.extend(this, Backbone.Events)

    this.removeContentUpdater = listenToSelector((state) => {
      // if any of these changed, regenerate content
      return {
        ready: state[appSlice.name].ready,
        isLoggedIn: profileSlice.selectIsLoggedIn(state),
        page: state[appSlice.name].page
      }
    }, (obj) => {
      // page changed
      const pageChanged = this._page !== obj.page
      this._page = obj.page

      // regenerate content
      updatePage.call(this, this.state, this._page)

      if (pageChanged && this._page === 'logout') {
        destroySession()
      }
    }, (oldState, newState) => {
      // comparator
      return JSON.stringify(oldState) === JSON.stringify(newState)
    })
  }

  async connectedCallback () {
    await initialize.call(this)
    super.connectedCallback()
  }

  stateChanged (state) {
    this.role = state[appSlice.name].role

    toggleReady.call(this)
  }

  updated (changedProps) {
    // Ensure any prior content Backbone views are properly removed
    if (changedProps.has('content')) {
      changedProps.get('content')?.remove?.()
    }
  }

  render () {
    const state = store.getState()
    const { mode, page } = state[appSlice.name]

    // Set the active navigation path
    let activePath = page
    if (this.activePath) {
      const pathParts = this.activePath.split('/')
      // Only use the override property if the root path matches the current page
      if (pathParts[0] === page) {
        activePath = this.activePath
      }
    }

    return html`
        <notification-manager></notification-manager>
        <div class=${bem()}>
          <nav-wrapper
            @activated=${handleActivated.bind(this)}
            active=${ifDefined(activePath)}
            class=${bem('nav')}
            mode=${mode}
          ></nav-wrapper>
          <div class=${bem('content')}>
            ${renderContent.call(this)}
          </div>
        </div>
      `
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    if (this.removeContentUpdater) {
      this.removeContentUpdater()
    }

    if (this.removeActivitiesPathListener) {
      this.removeActivitiesPathListener()
    }

    if (this.content instanceof Backbone.View) {
      this.content.remove()
    }

    if (this.state) {
      this.stopListening(this.state)
      this.state.off()
      this.state.destroy?.()
    }

    // clear routers
    if (Backbone.history) {
      Backbone.history.handlers = []
    }
  }
}

function renderContent () {
  return this.content instanceof Backbone.View
    ? this.content.render().el
    : this.content
}

const bem = bemBlock(App.tagName)
register(module, App.tagName, App)
